import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import customParseFormat from "dayjs/plugin/customParseFormat";

import * as QueryKeys from "data";
import type { DeploymentInfo } from "data/models";
import { fetchDeploymentStatus } from "data/queries";
import React, { useState } from "react";
import Draggable from "react-draggable";
import { useQuery } from "@tanstack/react-query";

dayjs.extend(relativeTime);
dayjs.extend(customParseFormat);

const DeploymentCard: React.FC<{
  deployment: DeploymentInfo;
  title: string;
}> = ({ deployment, title }) => {
  const isUpToDate = deployment.is_latest_on_branch ?? true;

  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography
        variant="caption"
        display="block"
        color="text.secondary"
        sx={{ mb: 0.5, fontSize: "0.65rem" }}
      >
        {title}
      </Typography>
      <Typography
        variant="body1"
        sx={{ fontFamily: "monospace", fontWeight: 600, mb: 0.5 }}
      >
        {deployment.branch_name}
      </Typography>
      {!isUpToDate && deployment.commits_behind_branch && (
        <Typography
          variant="caption"
          color="warning.main"
          sx={{ display: "block", mb: 0.5 }}
        >
          ⚠️ {deployment.commits_behind_branch} commit
          {deployment.commits_behind_branch > 1 ? "s" : ""} behind
        </Typography>
      )}
      {isUpToDate && (
        <Typography
          variant="caption"
          color="success.main"
          sx={{ display: "block", mb: 0.5 }}
        >
          ✓ Up to date
        </Typography>
      )}
      <Typography
        variant="caption"
        sx={{ fontSize: "0.65rem", color: "text.secondary" }}
      >
        Build {deployment.build_number}
        {deployment.commit_hash &&
          ` • ${deployment.commit_hash.substring(0, 7)}`}
      </Typography>
      {deployment.finish_date && (
        <Typography
          variant="caption"
          sx={{
            fontSize: "0.65rem",
            color: "text.secondary",
            display: "block",
            mt: 0.5,
          }}
        >
          {`Deployed ${dayjs(deployment.finish_date, "YYYYMMDDTHHmmssZ").format(
            "MMMM D, YYYY h:mm A"
          )} (${dayjs(deployment.finish_date, "YYYYMMDDTHHmmssZ").fromNow()})`}
        </Typography>
      )}
    </Box>
  );
};

const DeploymentStatusWidget: React.FC = () => {
  const [expanded, setExpanded] = useState(false);
  const [minimized, setMinimized] = useState(false);

  // Only show in preprod
  const environment = import.meta.env.VITE_ENVIRONMENT;
  const shouldShow = environment === 'preprod';

  const frontendQuery = useQuery({
      queryKey: [QueryKeys.deploymentStatus, "hub_frontend"],
      queryFn: () => fetchDeploymentStatus("hub_frontend"),
      enabled: shouldShow,
      refetchInterval: 5 * 60 * 1000,
      staleTime: 4 * 60 * 1000,
    });

  const backendQuery = useQuery({
      queryKey: [QueryKeys.deploymentStatus, "hub_backend_api"],
      queryFn: () => fetchDeploymentStatus("hub_backend_api"),
      enabled: shouldShow,
      refetchInterval: 5 * 60 * 1000,
      staleTime: 4 * 60 * 1000,
    });

  if (!shouldShow) {
    return null;
  }

  if (frontendQuery.isPending || backendQuery.isPending) {
    return null;
  }

  if (frontendQuery.isError || backendQuery.isError) {
    return null;
  }

  const frontendInfo = frontendQuery.data;
  const backendInfo = backendQuery.data;

  if (!frontendInfo || !backendInfo) {
    return null;
  }

  const anyOutdated =
    !(frontendInfo.is_latest_on_branch ?? true) ||
    !(backendInfo.is_latest_on_branch ?? true);

  if (minimized) {
    return (
      <Draggable bounds="body" handle=".drag-handle">
        <Paper
          elevation={3}
          sx={{
            position: "fixed",
            bottom: 16,
            right: 16,
            zIndex: 9999,
            cursor: "pointer",
          }}
          onClick={() => setMinimized(false)}
        >
          <Box className="drag-handle" sx={{ p: 0.5 }}>
            <Chip
              label="Hub"
              color={anyOutdated ? "warning" : "success"}
              size="small"
              sx={{ fontSize: "0.65rem" }}
            />
          </Box>
        </Paper>
      </Draggable>
    );
  }

  return (
    <Draggable bounds="body" handle=".drag-handle">
      <Paper
        elevation={3}
        sx={{
          position: "fixed",
          bottom: 16,
          right: 16,
          zIndex: 9999,
          minWidth: 250,
          maxWidth: 400,
        }}
      >
        <Box
          className="drag-handle"
          sx={{
            p: 1,
            display: "grid",
            gridTemplateColumns: "auto 1fr auto auto",
            gap: 1,
            alignItems: "center",
            cursor: "move",
          }}
        >
          <Box
            sx={{
              display: "contents",
              cursor: "pointer",
            }}
            onClick={() => setExpanded(!expanded)}
          >
            <Typography
              variant="caption"
              sx={{ fontSize: "0.65rem", color: "text.secondary" }}
            >
              FE:
            </Typography>
            <Chip
              label={frontendInfo.branch_name}
              color={
                frontendInfo.is_latest_on_branch ?? true ? "success" : "warning"
              }
              size="small"
              sx={{ fontFamily: "monospace", fontSize: "0.7rem" }}
            />
            <IconButton size="small" sx={{ p: 0, gridRow: "span 2" }}>
              {expanded ? (
                <ExpandLessIcon fontSize="small" />
              ) : (
                <ExpandMoreIcon fontSize="small" />
              )}
            </IconButton>

            <Typography
              variant="caption"
              sx={{
                fontSize: "0.65rem",
                color: "text.secondary",
                gridColumn: 1,
              }}
            >
              BE:
            </Typography>
            <Chip
              label={backendInfo.branch_name}
              color={
                backendInfo.is_latest_on_branch ?? true ? "success" : "warning"
              }
              size="small"
              sx={{
                fontFamily: "monospace",
                fontSize: "0.7rem",
                gridColumn: 2,
              }}
            />
          </Box>
          <IconButton
            size="small"
            sx={{ p: 0, gridRow: "span 2", gridColumn: 4 }}
            onClick={(e) => {
              e.stopPropagation();
              setMinimized(true);
            }}
            title="Minimize"
          >
            <Typography sx={{ fontSize: "0.8rem" }}>−</Typography>
          </IconButton>
        </Box>

        <Collapse in={expanded}>
          <Box sx={{ p: 2, pt: 0, borderTop: 1, borderColor: "divider" }}>
            <DeploymentCard deployment={frontendInfo} title="Frontend" />
            <Divider sx={{ my: 1 }} />
            <DeploymentCard deployment={backendInfo} title="Backend API" />
          </Box>
        </Collapse>
      </Paper>
    </Draggable>
  );
};

export default DeploymentStatusWidget;
